import React from 'react';
import { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent, successContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, extLnkNotificationPref } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { selectIsAuthed } from 'store/customer/customer.selectors';
import { deleteSession } from 'store/customer/customer.thunk';
import { clearNotification, createNotification } from 'store/notification';
import { AppDispatch, useAppDispatch, useAppSelector } from 'store/store';

import { clearShipmentList } from '../../../store/shipment/list/shipmentList.slice';
import axiosInstance from '../../../utils/axios.utils';
import { useShipment } from './shipment.hooks';

const authedUrl = `${apiEndpoint.notificationSubscriptionAuthed}/`;
const requestConfig = { withCredentials: true };

const apiPostReceiveEmailUpdatesAuthed = (consignmentId: string) => {
  return axiosInstance.post(authedUrl + consignmentId, {}, requestConfig);
};

const apiDeleteCancelEmailUpdatesAuthed = (consignmentId: string) => {
  return axiosInstance.delete(authedUrl + consignmentId, requestConfig);
};

export const useNotificationSubscriptionAuthed = () => {
  const dispatch = useAppDispatch();
  const consignmentId = useShipment()?.consignmentId || '';
  const isAuth = useAppSelector(selectIsAuthed);

  const [loading, setLoading] = React.useState(false);

  const enableNotificationSubscription = async () => {
    let subscribed = false;
    if (!isAuth || !consignmentId || loading) return subscribed;

    setLoading(true);
    subscribed = true;

    await apiPostReceiveEmailUpdatesAuthed(consignmentId)
      .then(() => {
        // Success status: 201
        handleSubscriptionSuccess(
          dispatch,
          NotificationStatus.SUCCESS,
          successContent.articleSubscription.enabled + createNotificationSuffix(isAuth, consignmentId)
        );
      })
      .catch((err) => {
        const authError = err as AuthError;
        if (authError?.isReauthRequiredError?.() || authError?.isLoginRequiredError?.()) {
          dispatch(clearNotification());
          dispatch(
            createNotification({
              type: NotificationType.ALERT,
              status: NotificationStatus.INFO,
              message: errorContent.expiredSession,
            })
          );
          dispatch(clearShipmentList());
          return dispatch(deleteSession());
        }

        subscribed = false;
        const axiosErr = err as AxiosError;
        const status = axiosErr.response?.status;
        handleSubscriptionError(dispatch, status);
      })
      .finally(() => setLoading(false));

    return subscribed;
  };

  const disableNotificationSubscription = async () => {
    let subscribed = true;

    if (!isAuth || !consignmentId || loading) return subscribed;

    setLoading(true);
    subscribed = false;

    await apiDeleteCancelEmailUpdatesAuthed(consignmentId)
      .then(() => {
        // Success status: 201
        handleSubscriptionSuccess(
          dispatch,
          NotificationStatus.INFO,
          successContent.articleSubscription.disabled + createNotificationSuffix(isAuth, consignmentId)
        );
      })
      .catch((err) => {
        const authError = err as AuthError;
        if (authError?.isReauthRequiredError?.() || authError?.isLoginRequiredError?.()) {
          dispatch(clearNotification());
          dispatch(
            createNotification({
              type: NotificationType.ALERT,
              status: NotificationStatus.INFO,
              message: errorContent.expiredSession,
            })
          );
          dispatch(clearShipmentList());
          return dispatch(deleteSession());
        }
        subscribed = true;
        const axiosErr = err as AxiosError;
        const status = axiosErr.response?.status;
        handleSubscriptionError(dispatch, status);
      })
      .finally(() => setLoading(false));

    return subscribed;
  };

  return { enableNotificationSubscription, disableNotificationSubscription, loading };
};

const handleSubscriptionSuccess = (dispatch: AppDispatch, status: NotificationStatus, message: string) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: status,
    message: message,
  };
  dispatch(createNotification(notification));
  return notification;
};

const handleSubscriptionError = (dispatch: AppDispatch, status?: number) => {
  const notification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.ERROR,
    message: errorContent.articleSubscriptionError,
  };

  if (status === 401) {
    dispatch(clearShipmentList());
    dispatch(deleteSession());
    notification.status = NotificationStatus.INFO;
    notification.message = errorContent.expiredSession;
  }
  dispatch(createNotification(notification));
  return notification;
};

const createNotificationSuffix = (isAuth: boolean, trackingNumber: string) => {
  if (isAuth) {
    const redirectUrl =
      extLnkNotificationPref + '/?entry=TRACK&next=' + encodeURIComponent('/mypost/track/details/' + trackingNumber);
    return ` <a href="${redirectUrl}" target="_blank">View notification preferences</a>`;
  }
  return '';
};
